import { Alert, Button, Checkbox, Form, Input, Modal, Popconfirm, Radio, Select, Space, TimePicker } from "antd";
import { useCallback, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import utc from 'dayjs/plugin/utc';
import LocalizedDatePicker from "../../util/localized-date-picker";
import PlamoApiWithSaveNotifications from '../../util/with-save-notification'
import { useGetCustomersQuery } from "../../../../api/plamo-api";

dayjs.extend(utc)

const { useCreateCalendarSeriesWithSaveNotificationMutation, useUpdateCalendarSeriesWithSaveNotificationMutation, useUpdateCalendarEntryWithSaveNotificationMutation } = PlamoApiWithSaveNotifications;

// Customer id used in the form for a stand-in customer, saved as series without customer
const SUBSTITUTE_CUSTOMER_ID = 0;
// End date used for series without end date, same as in the app
const NO_END_DATE = '2036-01-01';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const toUtcString = (d: Dayjs) => d.utc().format('YYYY-MM-DDTHH:mm:ss') + '.000Z';

const combine = (date: Dayjs, time: Dayjs) => date.hour(time.hour()).minute(time.minute()).second(0).millisecond(0);

const weekdayIndex = (d: Dayjs) => (d.day() + 6) % 7;

const isRecurring = (series: any) => !!series?.pattern?.length;

// Series times are stored as UTC hours and minutes
const seriesTime = (series: any, hours: number, minutes: number) => dayjs.utc(series.startDate).hour(hours).minute(minutes).local();

const entryValues = (entry: any) => ({ date: dayjs(entry.startTime), startTime: dayjs(entry.startTime), endTime: dayjs(entry.endTime) });

const seriesValues = (series: any) => ({
  date: dayjs(series.startDate),
  startTime: seriesTime(series, series.startTimeHours, series.startTimeMinutes),
  endTime: seriesTime(series, series.endTimeHours, series.endTimeMinutes),
});

const initialValuesFor = (entry: any, series: any, date: Dayjs | undefined) => {
  if (!entry) {
    return { date, startTime: date?.hour(8).minute(0), endTime: date?.hour(9).minute(0), recurring: false, frequency: 'w', weekdays: date ? [weekdayIndex(date)] : [] };
  }
  const pattern: string = series?.pattern || '';
  const endDate = series && dayjs(series.endDate).format('YYYY-MM-DD') !== NO_END_DATE ? dayjs(series.endDate) : undefined;
  return {
    ...entryValues(entry),
    scope: 'entry',
    customerId: series?.customerId ?? SUBSTITUTE_CUSTOMER_ID,
    substitutionCustomerFirstName: series?.substitutionCustomerFirstName,
    substitutionCustomerLastName: series?.substitutionCustomerLastName,
    frequency: pattern.includes('b') ? 'b' : 'w',
    weekdays: WEEKDAYS.map((_, idx) => idx).filter(idx => pattern.includes(`${idx}`)),
    endDate,
  };
}

// Add (date set) or edit (entry and its series set) a calendar entry or series of an employee
function CalendarEntryModal({ employeeId, entry, series, date, onClose }: any) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState<boolean>(false);
  const open = !!(entry || date);

  const scope = Form.useWatch('scope', form);
  const recurring = Form.useWatch('recurring', form);
  const customerId = Form.useWatch('customerId', form);

  const existingRecurring = !!entry && isRecurring(series);
  const editEntryOnly = existingRecurring && scope !== 'series';
  const editSeries = existingRecurring && scope === 'series';
  const showRecurrence = editSeries || (!entry && recurring);

  const { data: customers } = useGetCustomersQuery({ c: 10000, o: 0, f: "{}" }, { skip: !open }) as any;
  const customerOptions = useMemo(() => [
    ...(customers?.items || []).map(c => ({ value: c.id, label: `${c.firstName} ${c.lastName}` })),
    { value: SUBSTITUTE_CUSTOMER_ID, label: 'Vertretungskunde' },
  ], [customers]);

  const [createCalendarSeries] = useCreateCalendarSeriesWithSaveNotificationMutation();
  const [updateCalendarSeries] = useUpdateCalendarSeriesWithSaveNotificationMutation();
  const [updateCalendarEntry] = useUpdateCalendarEntryWithSaveNotificationMutation();

  const initialValues = useMemo(() => initialValuesFor(entry, series, date), [entry, series, date]);

  const onValuesChange = useCallback((changed: any) => {
    if (changed.scope) {
      form.setFieldsValue(changed.scope === 'series' ? seriesValues(series) : entryValues(entry));
    }
  }, [form, entry, series]);

  const finish = useCallback(async (result: any) => {
    setSaving(false);
    if (result && !result.error) {
      onClose();
    }
  }, [onClose]);

  const save = useCallback(async () => {
    const values = await form.validateFields();
    const start = combine(values.date, values.startTime);
    const end = combine(values.date, values.endTime);
    setSaving(true);

    if (editEntryOnly) {
      finish(await updateCalendarEntry({ ...entry, startTime: toUtcString(start), endTime: toUtcString(end) }));
      return;
    }

    const substitute = values.customerId === SUBSTITUTE_CUSTOMER_ID;
    // Single entries are stored as a series without pattern, same as in the app
    const newSeries = {
      ...series,
      employeeId,
      customerId: substitute ? null : values.customerId,
      substitutionCustomerFirstName: substitute ? values.substitutionCustomerFirstName : null,
      substitutionCustomerLastName: substitute ? values.substitutionCustomerLastName : null,
      startDate: toUtcString(start),
      endDate: showRecurrence
        ? toUtcString(combine(values.endDate || dayjs(NO_END_DATE), values.endTime))
        : toUtcString(start.add(1, 'day')),
      startTimeHours: start.utc().hour(),
      startTimeMinutes: start.utc().minute(),
      endTimeHours: end.utc().hour(),
      endTimeMinutes: end.utc().minute(),
      pattern: showRecurrence ?[...values.weekdays].sort().join('') + values.frequency : '',
    };

    finish(await (series ? updateCalendarSeries(newSeries) : createCalendarSeries(newSeries)));
  }, [form, entry, series, employeeId, editEntryOnly, showRecurrence, finish, createCalendarSeries, updateCalendarSeries, updateCalendarEntry]);

  const doDelete = useCallback(async () => {
    setSaving(true);
    if (editSeries) {
      // Entries before the cancellation date are kept, later ones are removed
      finish(await updateCalendarSeries({ ...series, cancelled: toUtcString(dayjs(entry.originalStartTime || entry.startTime).startOf('day')) }));
    } else {
      // Entries of a series are regenerated when deleted, so they are cancelled instead
      finish(await updateCalendarEntry({ ...entry, cancelled: true }));
    }
  }, [entry, series, editSeries, finish, updateCalendarEntry, updateCalendarSeries]);

  const validateEndTime = useCallback(async (_, value: Dayjs) => {
    const startTime = form.getFieldValue('startTime');
    if (value && startTime && !combine(dayjs(), value).isAfter(combine(dayjs(), startTime))) {
      throw new Error('Das Ende muss nach dem Beginn liegen');
    }
  }, [form]);

  const validateEndDate = useCallback(async (_, value: Dayjs) => {
    const startDate = form.getFieldValue('date');
    if (value && startDate && value.isBefore(startDate, 'day')) {
      throw new Error('Das Enddatum darf nicht vor dem Startdatum sein');
    }
  }, [form]);

  const deleteDate = entry && dayjs(entry.originalStartTime || entry.startTime).format('DD.MM.YYYY');

  const footer = <Space>
    { entry && <Popconfirm
      title={editSeries ? 'Serie beenden?' : 'Termin löschen?'}
      description={editSeries ? `Alle Termine der Serie ab dem ${deleteDate} werden gelöscht. Frühere Termine bleiben erhalten.` : 'Der Termin wird für den Mitarbeiter abgesagt.'}
      okText="Löschen"
      cancelText="Abbrechen"
      onConfirm={doDelete}
    >
      <Button danger disabled={saving}>{ editSeries ? 'Serie löschen' : 'Löschen' }</Button>
    </Popconfirm> }
    <Button onClick={onClose} disabled={saving}>Abbrechen</Button>
    <Button type="primary" onClick={save} loading={saving}>Speichern</Button>
  </Space>;

  return <Modal title={entry ? 'Termin bearbeiten' : 'Termin hinzufügen'} open={open} onCancel={onClose} footer={footer} destroyOnClose>
    <Form form={form} layout="vertical" preserve={false} initialValues={initialValues} onValuesChange={onValuesChange}>
      { existingRecurring && <Form.Item name="scope">
        <Radio.Group optionType="button" options={[{ value: 'entry', label: 'Nur dieser Termin' }, { value: 'series', label: 'Gesamte Serie' }]} />
      </Form.Item> }
      { editSeries && <Alert type="warning" showIcon style={{ marginBottom: 16 }} message="Beim Speichern der Serie gehen einzeln geänderte oder abgesagte Termine dieser Serie verloren." /> }
      <Form.Item name="customerId" label="Kunde" rules={[{ required: true, message: 'Bitte einen Kunden auswählen' }]}>
        <Select
          showSearch
          disabled={editEntryOnly}
          options={customerOptions}
          filterOption={(input, option) => `${option?.label ?? ''}`.toLowerCase().includes(input.toLowerCase())}
        />
      </Form.Item>
      { customerId === SUBSTITUTE_CUSTOMER_ID && <Space>
        <Form.Item name="substitutionCustomerFirstName" label="Vorname" rules={[{ required: true, message: 'Bitte Vorname angeben' }]}>
          <Input disabled={editEntryOnly} />
        </Form.Item>
        <Form.Item name="substitutionCustomerLastName" label="Nachname" rules={[{ required: true, message: 'Bitte Nachname angeben' }]}>
          <Input disabled={editEntryOnly} />
        </Form.Item>
      </Space> }
      <Form.Item name="date" label={showRecurrence ? 'Beginn der Serie' : 'Datum'} rules={[{ required: true, message: 'Bitte ein Datum auswählen' }]}>
        <LocalizedDatePicker format="DD.MM.YYYY" allowClear={false} style={{ width: '100%' }} />
      </Form.Item>
      <Space>
        <Form.Item name="startTime" label="Von" rules={[{ required: true, message: 'Bitte Beginn angeben' }]}>
          <TimePicker format="HH:mm" minuteStep={5} allowClear={false} />
        </Form.Item>
        <Form.Item name="endTime" label="Bis" dependencies={['startTime']} rules={[{ required: true, message: 'Bitte Ende angeben' }, { validator: validateEndTime }]}>
          <TimePicker format="HH:mm" minuteStep={5} allowClear={false} />
        </Form.Item>
      </Space>
      { !entry && <Form.Item name="recurring" valuePropName="checked">
        <Checkbox>Serientermin</Checkbox>
      </Form.Item> }
      { showRecurrence && <>
        <Form.Item name="frequency" label="Wiederholung">
          <Radio.Group options={[{ value: 'w', label: 'wöchentlich' }, { value: 'b', label: '14-tägig' }]} />
        </Form.Item>
        <Form.Item name="weekdays" label="Wochentage" rules={[{ required: true, message: 'Bitte mindestens einen Wochentag auswählen' }]}>
          <Checkbox.Group options={WEEKDAYS.map((label, idx) => ({ value: idx, label }))} />
        </Form.Item>
        <Form.Item name="endDate" label="Enddatum" dependencies={['date']} rules={[{ validator: validateEndDate }]}>
          <LocalizedDatePicker format="DD.MM.YYYY" placeholder="Kein Enddatum" style={{ width: '100%' }} />
        </Form.Item>
      </> }
    </Form>
  </Modal>;
}

export default CalendarEntryModal;
